import { useMemo, useState, useEffect } from 'react';
import QuestionForm from '../forms/QuestionForm';
import SectionForm from '../forms/SectionForm';
import ActionForm from '../forms/ActionForm';
import DecisionForm from '../forms/DecisionForm';
import { useSelectedNodeId } from '../../contexts/useSelectedNodeId';
import { useNodesEdges } from '../../contexts/useNodesEdges';
import type {
    QuestionNodeData,
    DecisionNodeData,
    SectionNodeData,
    ActionNodeData,
} from '@app/services/aiFlow.types';
import { Drawer } from 'antd';

function EditNodeDrawer() {
    const { selectedNodeId, setSelectedNodeId } = useSelectedNodeId();
    const { nodes, setNodes, setEdges, flowOrder } = useNodesEdges();
    const selectedNode = nodes.find((n) => n.id === selectedNodeId);
    const memoizedData = useMemo(() => selectedNode?.data, [selectedNode?.data]);
    const [openDrawer, setOpenDrawer] = useState(!!selectedNodeId);

    useEffect(() => {
        if (selectedNodeId) {
            const timeout = setTimeout(() => setOpenDrawer(true), 0);
            return () => clearTimeout(timeout);
        }
    }, [selectedNodeId]);

    if (!selectedNode) return null;

    const handleClose = () => {
        setOpenDrawer(false);
    };

    const num = flowOrder.find((i) => i.id === selectedNode.id)?.number ?? null;

    const handleSubmit = (
        data: ActionNodeData | DecisionNodeData | SectionNodeData | QuestionNodeData
    ) => {
        setNodes((nds) => {
            return nds.map((n) => {
                if (n.id !== selectedNode.id) return n;
                switch (n.type) {
                    case 'action':
                        return {
                            ...n,
                            data: {
                                ...n.data,
                                ...(data as ActionNodeData),
                            },
                        };
                    case 'decision':
                        return {
                            ...n,
                            data: {
                                ...n.data,
                                ...(data as DecisionNodeData),
                            },
                        };
                    case 'section':
                        return {
                            ...n,
                            data: {
                                ...n.data,
                                ...(data as SectionNodeData),
                            },
                        };
                    case 'question':
                        return {
                            ...n,
                            data: {
                                ...n.data,
                                ...(data as QuestionNodeData),
                            },
                        };
                    default:
                        return n;
                }
            });
        });

        if (memoizedData && memoizedData?.visConditionSlots > Number(data.visConditionSlots)) {
            setEdges((prev) => {
                return prev.filter((edge) => {
                    if (
                        edge.target === selectedNode.id &&
                        edge.targetHandle.startsWith('condition-target')
                    ) {
                        const conditionNum = edge.targetHandle.split('-')[2];
                        return Number(conditionNum) <= Number(data.visConditionSlots);
                    } else {
                        return true;
                    }
                });
            });
        }
        handleClose();
    };

    const selectFirstFormInput = () => {
        const firstInput = document.querySelectorAll('.edit-panel textarea')[0] as
            | HTMLTextAreaElement
            | undefined;
        if (firstInput) {
            firstInput.focus();
        }
    };

    const handleAfterOpenChange = (open: boolean) => {
        if (open) {
            selectFirstFormInput();
        } else {
            setSelectedNodeId(null);
        }
    };

    const renderForm = () => {
        switch (selectedNode.type) {
            case 'action':
                return (
                    <ActionForm
                        id={selectedNode.id}
                        data={memoizedData as ActionNodeData}
                        number={num}
                        onSubmit={handleSubmit}
                        onCancel={handleClose}
                    />
                );
            case 'decision':
                return (
                    <DecisionForm
                        id={selectedNode.id}
                        data={memoizedData as DecisionNodeData}
                        number={num}
                        onSubmit={handleSubmit}
                        onCancel={handleClose}
                    />
                );
            case 'section':
                return (
                    <SectionForm
                        id={selectedNode.id}
                        data={memoizedData as SectionNodeData}
                        number={num}
                        onSubmit={handleSubmit}
                        onCancel={handleClose}
                    />
                );
            case 'question':
                return (
                    <QuestionForm
                        id={selectedNode.id}
                        data={memoizedData as QuestionNodeData}
                        number={num}
                        onSubmit={handleSubmit}
                        onCancel={handleClose}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <Drawer
            mask={false}
            placement="right"
            closable={{ 'aria-label': 'Close Button' }}
            onClose={handleClose}
            open={openDrawer}
            getContainer={false}
            afterOpenChange={handleAfterOpenChange}>
            <div className={`edit-panel ${selectedNode.type}`}>{renderForm()}</div>
        </Drawer>
    );
}

export default EditNodeDrawer;
